import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { API_BASE } from './api';
import { useAuth } from './AuthContext';

export interface SpMarketplace {
  id: number;
  code: string;
  domain: string;
  region_name: string | null;
  sp_api_region: string | null;
  amazon_marketplace_id: string | null;
}

export interface SpAccount {
  seller_id: string;
  account_name: string;
  region: string;
  is_active: boolean;
  is_default: boolean;
  last_verified_at: string | null;
  created_at: string;
  marketplaces: SpMarketplace[];
}

interface SpAccountContextValue {
  accounts: SpAccount[];
  activeAccount: SpAccount | null;
  activeMarketplaceId: number | null;
  setActiveAccount: (a: SpAccount) => void;
  setActiveMarketplaceId: (id: number | null) => void;
  reload: () => Promise<void>;
  loading: boolean;
}

const SpAccountContext = createContext<SpAccountContextValue>({
  accounts: [],
  activeAccount: null,
  activeMarketplaceId: null,
  setActiveAccount: () => {},
  setActiveMarketplaceId: () => {},
  reload: async () => {},
  loading: false,
});

export const SpAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<SpAccount[]>([]);
  const [activeAccount, setActiveAccount] = useState<SpAccount | null>(null);
  const [activeMarketplaceId, setActiveMarketplaceId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data: SpAccount[] = await res.json();
      setAccounts(data);
      setActiveAccount(prev => {
        if (prev) {
          const refreshed = data.find(a => a.seller_id === prev.seller_id);
          return refreshed ?? prev;
        }
        return data.find(a => a.is_default) ?? data[0] ?? null;
      });
    } catch {
      // backend unreachable — stay silent, show empty state
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-load accounts whenever the authenticated user changes (covers fresh login).
  // On logout, clear all state immediately.
  useEffect(() => {
    if (!user) {
      setAccounts([]);
      setActiveAccount(null);
      setActiveMarketplaceId(null);
      return;
    }
    reload();
  }, [user, reload]);

  return (
    <SpAccountContext.Provider value={{
      accounts, activeAccount, activeMarketplaceId,
      setActiveAccount, setActiveMarketplaceId,
      reload, loading,
    }}>
      {children}
    </SpAccountContext.Provider>
  );
};

export const useSpAccount = () => useContext(SpAccountContext);
